import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from '../../../shared/hooks/utils';

export const BaseAiCallSchema = z.object({
  status: z.string(),
  to: z.string(),
  feedback: z
    .object({
      summary: z.string().nullable(),
      endReason: z.string(),
      outcome: z.string().nullable(),
      startedAt: z.iso.datetime().transform(dayjs).nullable(),
      endedAt: z
        .string()
        .transform(val => dayjs(val))
        .nullable(),
      recordingUrl: z.url().nullable(),
      transcript: z.string().nullable(),
    })
    .nullable(),
});

export const AiCallSchema = BaseAiCallSchema.extend({
  id: z.uuid(),
  retryCount: z.number().default(0),
  debtorName: z.string(),
  debtorId: z.coerce.string(),
  invoiceReference: z.string(),
});

export const ReminderAiCallSchema = BaseAiCallSchema.nullable();

export type BaseAiCall = z.infer<typeof BaseAiCallSchema>;
export type AiCall = z.infer<typeof AiCallSchema>;
export type ReminderAiCall = z.infer<typeof ReminderAiCallSchema>;

export type LoadAiCallVariables = {
  id: string;
};

export async function loadAiCallFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadAiCallVariables,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get(`/ai_calls/${variables.id}`);

  const parseResult = AiCallSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'aiCall');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyAiCall: AiCall = {
  id: '',
  status: '',
  to: '',
  invoiceReference: '',
  debtorName: '',
  debtorId: '',
  retryCount: 0,
  feedback: {
    summary: '',
    endReason: '',
    outcome: '',
    startedAt: null,
    endedAt: null,
    recordingUrl: null,
    transcript: '',
  },
};

export function useLoadAiCall(variables: LoadAiCallVariables, options: QueryOptions<AiCall> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<AiCall, ApiError>({
    queryKey: ['aiCall', variables],
    queryFn: context => loadAiCallFn(axiosInstance, context.queryKey[1] as LoadAiCallVariables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;

      return emptyAiCall;
    },
    ...options,
  });

  return addResourceNameToQueryResult<AiCall, ApiError, 'aiCall', 'PreventUndefined'>(
    'aiCall',
    queryResult,
    emptyAiCall,
  );
}
